/**
 * Attachment image loader.
 *
 * Attachment files are served from an endpoint that requires the same
 * `Authorization: Bearer <token>` header as the rest of the API, so a plain
 * `<img src={file_url}>` (no auth) or a hand-rolled `fetch` with a stale token
 * gets a 401 and renders as a broken image.
 *
 * Reuse the configured `taskmanAxios` instance — its request interceptor
 * already attaches a fresh access token (and handles refresh) — and hand the
 * caller back an object-URL pointing at the downloaded blob.
 */
import taskmanAxios from './axios'
import { readBlobBuffer } from '../utils/clipboardFiles'

const isImg = (fileName = '') =>
    /\.(jpe?g|png|webp|svg|gif|bmp|avif)(\?|$)/i.test(String(fileName))

const previewMimeTypes = {
    csv: 'text/csv;charset=utf-8',
    jpeg: 'image/jpeg',
    jpg: 'image/jpeg',
    json: 'application/json;charset=utf-8',
    md: 'text/markdown;charset=utf-8',
    pdf: 'application/pdf',
    png: 'image/png',
    txt: 'text/plain;charset=utf-8',
}

const getPreviewMimeType = (fileName = '', fallback = '') => {
    const cleanName = String(fileName).split(/[?#]/, 1)[0]
    const extension = cleanName.match(/\.([^.]+)$/)?.[1]?.toLowerCase()

    return previewMimeTypes[extension] || fallback
}

/**
 * Decide whether a downloaded blob really holds image bytes.
 *
 * The backend can answer a preview request with 200 and a non-image body — an
 * HTML error page, an S3 `AccessDenied`/`ExpiredToken` XML document, or an
 * empty response. Trusting the file name (or a `Content-Type` the storage
 * guessed) and relabelling such a body as `image/png` produces an object URL
 * the browser cannot decode, i.e. the broken-image icon. Sniff the magic bytes
 * instead so the caller can fall back to another source.
 */
const imageSignatures = [
    { type: 'image/png', bytes: [0x89, 0x50, 0x4e, 0x47] },
    { type: 'image/jpeg', bytes: [0xff, 0xd8, 0xff] },
    { type: 'image/gif', bytes: [0x47, 0x49, 0x46, 0x38] },
    { type: 'image/bmp', bytes: [0x42, 0x4d] },
]

const matchesSignature = (header, bytes, offset = 0) =>
    bytes.every((byte, index) => header[offset + index] === byte)

// SVG can start with an XML declaration, comments, or a doctype. Keep the
// read bounded, but include enough of the preamble to reach the root tag.
const readBlobHeader = (blob) => readBlobBuffer(blob.slice(0, 4096))

// Set by `sniffImageType` so a rejection can be reported with the bytes that
// were actually inspected, without reading the blob a second time.
let lastSniffedHeader = ''

const sniffImageType = async (blob) => {
    lastSniffedHeader = ''

    if (!blob || !blob.size) return null

    let header

    try {
        header = new Uint8Array(await readBlobHeader(blob))
    } catch (error) {
        console.error('Failed to read attachment header:', error)
        return null
    }

    const signature = imageSignatures.find(({ bytes }) => matchesSignature(header, bytes))
    if (signature) return signature.type

    // RIFF....WEBP
    if (matchesSignature(header, [0x52, 0x49, 0x46, 0x46])
        && matchesSignature(header, [0x57, 0x45, 0x42, 0x50], 8)) {
        return 'image/webp'
    }

    // ....ftypavif
    if (matchesSignature(header, [0x66, 0x74, 0x79, 0x70, 0x61, 0x76, 0x69, 0x66], 4)) {
        return 'image/avif'
    }

    // SVG is text, so look for the opening tag rather than a byte signature.
    const text = new TextDecoder().decode(header).trimStart()
    let svgCandidate = text
    let previousCandidate

    // Remove only valid SVG preamble constructs anchored at the start. This
    // accepts real SVG files without accepting an arbitrary HTML page that
    // happens to contain an inline <svg> later in its body.
    do {
        previousCandidate = svgCandidate
        svgCandidate = svgCandidate
            .replace(/^<\?xml[\s\S]*?\?>\s*/i, '')
            .replace(/^<!--[\s\S]*?-->\s*/, '')
            .replace(/^<!doctype\s+svg(?:\s[^>]*)?>\s*/i, '')
    } while (svgCandidate !== previousCandidate)

    if (/^<svg(?:\s|>)/i.test(svgCandidate)) return 'image/svg+xml'

    lastSniffedHeader = text
    return null
}

const getAttachmentUrlFromPayload = (payload) => payload?.file_url
    || payload?.url
    || payload?.data?.file_url
    || payload?.data?.url

const getJsonBlobAttachmentUrl = async (blob) => {
    if (!blob || !/application\/json/i.test(blob.type)) return null

    try {
        return getAttachmentUrlFromPayload(JSON.parse(await blob.text())) || null
    } catch (error) {
        console.error('Failed to parse attachment URL response:', error)
        return null
    }
}

/**
 * Normalize whatever the backend gave us into an *absolute* URL.
 *
 * The backend returns file paths already prefixed with `/api`
 * (e.g. `/api/attachments/1826/file`), while taskmanAxios.baseURL also ends
 * in `/api`. Passing the path relative makes axios prepend the base and
 * produce a doubled `/api/api/...` -> 404. So we resolve everything against
 * the API *origin* (scheme + host, no `/api`) and request the full URL.
 *
 * - absolute URL  -> use as-is
 * - relative path  -> resolve against the API origin
 */
const toRequestUrl = (fileUrl) => {
    const raw = String(fileUrl || '')
    if (/^https?:\/\//i.test(raw)) return raw

    try {
        const base = taskmanAxios.defaults.baseURL || ''
        const origin = new URL(base).origin
        return `${origin}${raw.startsWith('/') ? '' : '/'}${raw}`
    } catch {
        return raw
    }
}

const fetchAttachmentBlob = async (fileUrl) => {
    if (!fileUrl) return null
    const url = toRequestUrl(fileUrl)

    try {
        const res = await taskmanAxios.get(url, { responseType: 'blob' })
        return res.data
    } catch (e) {
        // e is the axios *response* (see axios.js response interceptor)
        console.error('Failed to load attachment:', url, e?.status)
        return null
    }
}

const fetchPublicAttachmentBlob = async (fileUrl, visitedUrls = new Set()) => {
    if (!fileUrl) return null

    const url = String(fileUrl)

    if (visitedUrls.has(url)) return null
    visitedUrls.add(url)

    try {
        const response = await fetch(url, {
            headers: { Accept: '*/*' },
        })

        if (!response.ok) return null

        const responseBlob = await response.blob()

        if (/application\/json/i.test(responseBlob.type)) {
            const nextUrl = await getJsonBlobAttachmentUrl(responseBlob)
            return nextUrl
                ? fetchPublicAttachmentBlob(nextUrl, visitedUrls)
                : null
        }

        return responseBlob
    } catch (error) {
        console.error('Failed to load public attachment:', error)
        return null
    }
}

const resolveAttachmentBlob = async (blob) => {
    if (!blob) return null
    if (!/application\/json/i.test(blob.type)) return blob

    const publicFileUrl = await getJsonBlobAttachmentUrl(blob)
    return publicFileUrl ? fetchPublicAttachmentBlob(publicFileUrl) : null
}

/**
 * Turn a downloaded blob into an object URL, but only when it really contains
 * image bytes. Returning `null` for anything else lets the caller try the next
 * source instead of handing the browser an undecodable `<img src>`.
 */
const toImageObjectUrl = async (blob) => {
    if (!blob) return null

    const imageType = await sniffImageType(blob)

    if (!imageType) {
        // The sniffed header tells at a glance whether the server sent an HTML
        // error page, a JSON envelope, or truncated bytes.
        console.error(
            'Attachment response is not an image:',
            { type: blob.type, size: blob.size, startsWith: lastSniffedHeader },
        )
        return null
    }

    const imageBlob = blob.type === imageType
        ? blob
        : new Blob([blob], { type: imageType })

    return URL.createObjectURL(imageBlob)
}

/**
 * Fetch a protected image and return it as an object URL.
 */
export async function fetchAttachmentObjectUrl(fileUrl) {
    return toImageObjectUrl(await resolveAttachmentBlob(await fetchAttachmentBlob(fileUrl)))
}

/**
 * Fetch a public/signed attachment URL without an Authorization header and
 * return an object URL that can safely be used as an image source.
 */
export async function fetchPublicAttachmentObjectUrl(fileUrl) {
    return toImageObjectUrl(await fetchPublicAttachmentBlob(fileUrl))
}

/**
 * Fetch a protected document and normalize its MIME type for browser preview.
 */
export async function fetchAttachmentPreviewObjectUrl(fileUrl, fileName = '') {
    const responseBlob = await resolveAttachmentBlob(await fetchAttachmentBlob(fileUrl))

    if (!responseBlob) return null

    const previewType = getPreviewMimeType(fileName, responseBlob.type)
    const previewBlob = previewType && responseBlob.type !== previewType
        ? new Blob([responseBlob], { type: previewType })
        : responseBlob

    return URL.createObjectURL(previewBlob)
}

export { getPreviewMimeType, isImg }
